// Pure Email-mutation helpers consumed by the Email editor screen. Per
// spec/build-plan.md P-032 row + tech-arch.md §2.5. Co-located here so
// email-editor.tsx fits under the OC §3.1 ≤200-LOC target. Mirrors
// template-editor-helpers.ts (P-031 / D-093) for the Template-side flow.
//
// **Same-reference no-op return.** Each helper returns a NEW Email when
// something changed, or the SAME reference (`return e`) when the
// operation was a no-op (missing instanceId, equal value). The store's
// `setState` no-op detection (D-082's `next === state` check) then
// skips listener fan-out, avoiding spurious re-renders. The same
// short-circuit also fires inside `updateEmail` in `factories.ts` so
// the Email-list never churns on a dead toggle.
//
// **P-037.30 D-138 — Block-manipulation helpers (add / remove / move)
// added for Email-level Block edits.** Mirrors the Template-side trio
// in `template-editor-helpers.ts`. Lets chapter staff add a one-off
// Block to an Email without modifying the source Template (no
// Template propagation; same Email-local divergence pattern as the
// existing `included` toggle). New Block instances seed `options`
// from `defaultOptions` + `inputs` from `defaultInputs` + `included:
// true`; a fresh `instanceId` via `newId()` avoids collision with
// Template-sourced ids (the Email's array can now mix both pools).

import { newId } from '../../lib/id'
import { blocks, type BlockId } from '../../registry/blocks'
import type { Email, EmailBlockInstance } from '../../store/schemas'

function nowIso(): string {
  return new Date().toISOString()
}

/**
 * Replace the per-Email `inputs` payload on the Block instance with
 * `instanceId`. Returns the same Email reference if no such instance
 * exists OR if `next` is referentially equal to the current inputs (the
 * common case when a Form's safeParse coalesces a no-op edit).
 */
export function setBlockInputs(e: Email, instanceId: string, next: unknown): Email {
  const idx = e.blocks.findIndex((b) => b.instanceId === instanceId)
  if (idx === -1) return e
  const current = e.blocks[idx]!
  if (current.inputs === next) return e
  const blocks = e.blocks.slice()
  blocks[idx] = { ...current, inputs: next }
  return { ...e, blocks, updatedAt: nowIso() }
}

/**
 * Flip the `included` flag on the Block instance with `instanceId`.
 * Returns the same Email reference if no such instance exists OR if the
 * flag is already at the requested value.
 *
 * `included === false` makes `prepareBlock` skip the Block at render
 * time (D-077); the form fields stay editable so staff can toggle back
 * without losing data. Visual dim happens at the BlockSection layer.
 */
export function setBlockIncluded(e: Email, instanceId: string, included: boolean): Email {
  const idx = e.blocks.findIndex((b) => b.instanceId === instanceId)
  if (idx === -1) return e
  const current = e.blocks[idx]!
  if (current.included === included) return e
  const blocks = e.blocks.slice()
  blocks[idx] = { ...current, included }
  return { ...e, blocks, updatedAt: nowIso() }
}

/**
 * Set the Email's display `name`. Returns the same Email reference if
 * `name` is identical (the per-keystroke dispatch from the inline-edit
 * title would otherwise churn the store's listener queue when the user
 * types and immediately backspaces back to the same value).
 *
 * Empty `name` is allowed at the schema layer post-D-135; the editor
 * renders `name || 'Untitled email'` so empty persists silently and
 * the placeholder copy lives at the render seam, never in storage.
 *
 * Mirror of `setTemplateName` in template-editor-helpers.ts. The
 * Email's `templateName` snapshot (per Tech Arch §2.5) is a separate
 * field and stays untouched here.
 */
export function setEmailName(e: Email, name: string): Email {
  if (e.name === name) return e
  return { ...e, name, updatedAt: nowIso() }
}

/**
 * Apply a new `subjectLine` to the Email. No-op when the value is
 * unchanged so the store's listener fan-out + the persistence-debounce
 * stay quiet on idle keystrokes (D-082 reference-equality short-circuit
 * upstream). Schema accepts the empty string per D-142 — the V2.2 Push
 * button is the gate that requires non-empty at P-048. Mirrors
 * `setEmailName`.
 */
export function setEmailSubjectLine(e: Email, subjectLine: string): Email {
  if (e.subjectLine === subjectLine) return e
  return { ...e, subjectLine, updatedAt: nowIso() }
}

/**
 * Apply a new `previewText` (preheader) to the Email. The schema field
 * is `.optional()`, so empty input clears the field rather than storing
 * an empty string — keeps round-tripped state minimal. No-op when
 * unchanged. Mirrors `setEmailName` + `setEmailSubjectLine`.
 */
export function setEmailPreviewText(e: Email, previewText: string): Email {
  if ((e.previewText ?? '') === previewText) return e
  if (previewText === '') {
    const { previewText: _drop, ...rest } = e
    return { ...rest, updatedAt: nowIso() }
  }
  return { ...e, previewText, updatedAt: nowIso() }
}

/**
 * Append a fresh Block instance to the Email's `blocks` array. Seeds
 * `options` from the BlockDefinition's `defaultOptions` (the Email
 * lacks an options-form, so the staff editor accepts the Block's
 * defaults) + `inputs` from `defaultInputs` + `included: true`. Fresh
 * `instanceId` via `newId()` (UUID — safe to mix with Template-sourced
 * ids in the same array). Returns the same Email reference if
 * `blockId` is not registered.
 *
 * Mirrors `addBlockToTemplate` in `template-editor-helpers.ts`; the
 * Email-shape spread differs only by carrying `inputs` + `included`.
 */
export function addBlockToEmail(e: Email, blockId: string): Email {
  const def = blocks[blockId as BlockId]
  if (def === undefined) return e
  const instance: EmailBlockInstance = {
    instanceId: newId(),
    blockId,
    options: def.defaultOptions,
    inputs: def.defaultInputs,
    included: true,
  }
  return { ...e, blocks: [...e.blocks, instance], updatedAt: nowIso() }
}

/**
 * Drop the Block instance with the given `instanceId`. Returns the
 * same Email reference if no such instance exists.
 *
 * Hard-deletes the per-Email `inputs` payload along with the instance
 * — same no-confirm semantics as the Template-side helper. Staff who
 * want to keep the content but hide the Block from the rendered
 * Email use the `included: false` toggle (`setBlockIncluded`) instead.
 */
export function removeBlockFromEmail(e: Email, instanceId: string): Email {
  if (!e.blocks.some((b) => b.instanceId === instanceId)) return e
  return {
    ...e,
    blocks: e.blocks.filter((b) => b.instanceId !== instanceId),
    updatedAt: nowIso(),
  }
}

/**
 * Swap the Block instance at `instanceId` with its neighbour in the
 * given direction (`-1` = up, `+1` = down). Returns the same Email
 * reference if the instance is missing or the move would go out of
 * bounds. Mirror of `moveBlockInTemplate`.
 */
export function moveBlockInEmail(
  e: Email,
  instanceId: string,
  delta: -1 | 1,
): Email {
  const idx = e.blocks.findIndex((b) => b.instanceId === instanceId)
  if (idx === -1) return e
  const target = idx + delta
  if (target < 0 || target >= e.blocks.length) return e
  const next = e.blocks.slice()
  const tmp = next[idx]!
  next[idx] = next[target]!
  next[target] = tmp
  return { ...e, blocks: next, updatedAt: nowIso() }
}
